/**
 * Editor Tools Validation System Types
 * Provides optional validation for editor tools
 */

import type { ValidationError } from '@common/shared-ui-layer/types/validation.types'

export { formatBytes, matchesFileType } from '@common/shared-ui-layer/types/validation.types'

/**
 * Image upload validation rules
 */
export interface ImageUploadValidation {
  /** Maximum number of files that can be uploaded */
  maxFiles?: number
  /** Maximum file size in bytes */
  maxFileSize?: number
  /** Minimum file size in bytes */
  minFileSize?: number
  /** Accepted MIME types (e.g., ['image/jpeg', 'image/png']) */
  acceptedTypes?: string[]
  /** Maximum image dimensions */
  maxDimensions?: {
    width: number
    height: number
  }
  /** Minimum image dimensions */
  minDimensions?: {
    width: number
    height: number
  }
  /** Custom validation function */
  customValidator?: (file: File) => {
    valid: boolean
    error?: string
  }
  /** Custom error handler */
  onValidationError?: (errors: ValidationError[]) => void
}

/**
 * Emoji picker validation rules
 */
export interface EmojiValidation {
  /** Maximum number of emojis allowed */
  maxCount?: number
  /** Allowed emoji categories */
  allowedCategories?: string[]
  /** List of blocked emojis */
  blockedEmojis?: string[]
  /** Custom validation function */
  customValidator?: (emoji: string) => {
    valid: boolean
    error?: string
  }
  /** Custom error handler */
  onValidationError?: (errors: ValidationError[]) => void
}

/**
 * Text content validation rules
 */
export interface ContentValidation {
  /** Maximum content length */
  maxLength?: number
  /** Minimum content length */
  minLength?: number
  /** Whether content is required */
  required?: boolean
  /** Regex pattern for content validation */
  pattern?: RegExp
  /** Custom validation function */
  customValidator?: (content: string) => {
    valid: boolean
    error?: string
  }
  /** Custom error handler */
  onValidationError?: (errors: ValidationError[]) => void
}

/**
 * Video upload validation rules
 */
export interface VideoUploadValidation {
  /** Maximum number of files allowed */
  maxFiles?: number
  /** Maximum file size in bytes */
  maxFileSize?: number
  /** Minimum file size in bytes */
  minFileSize?: number
  /** Accepted MIME types */
  acceptedTypes?: string[]
  /** Maximum video duration in seconds */
  maxDuration?: number
  /** Minimum video duration in seconds */
  minDuration?: number
  /** Custom validation function */
  customValidator?: (file: File) => {
    valid: boolean
    error?: string
  }
  /** Custom error handler */
  onValidationError?: (errors: ValidationError[]) => void
}

/**
 * Link validation rules
 */
export interface LinkValidation {
  /** List of allowed domains */
  allowedDomains?: string[]
  /** List of blocked domains */
  blockedDomains?: string[]
  /** Require HTTPS protocol */
  requireHttps?: boolean
  /** Validate URL format */
  validateUrl?: boolean
  /** Custom validation function */
  customValidator?: (url: string) => {
    valid: boolean
    error?: string
  }
  /** Custom error handler */
  onValidationError?: (errors: ValidationError[]) => void
}

/**
 * File upload validation rules (for documents/attachments)
 */
export interface FileUploadValidation {
  /** Maximum number of files that can be uploaded */
  maxFiles?: number
  /** Maximum file size in bytes */
  maxFileSize?: number
  /** Minimum file size in bytes */
  minFileSize?: number
  /** Accepted MIME types (e.g., ['application/pdf', 'text/csv']) */
  acceptedTypes?: string[]
  /** Accepted file extensions (e.g., ['.pdf', '.doc', '.docx']) */
  acceptedExtensions?: string[]
  /** Custom validation function */
  customValidator?: (file: File) => {
    valid: boolean
    error?: string
  }
  /** Custom error handler */
  onValidationError?: (errors: ValidationError[]) => void
}

/**
 * Complete editor validation configuration
 */
export interface EditorValidation {
  imageUpload?: ImageUploadValidation
  videoUpload?: VideoUploadValidation
  fileUpload?: FileUploadValidation
  // emoji?: EmojiValidation  // Removed for now, interface kept for future use
  content?: ContentValidation
  link?: LinkValidation
}
